/**
 * Certifications: the records themselves, and the reimbursement applications that reference them.
 *
 * ── Two concepts that must never merge ────────────────────────────────────────────────────
 *
 * A certification has a RESULT — the employee passed or failed the exam. A reimbursement
 * application has a STATUS — an approver accepted or declined paying for it. They look similar
 * on screen and they mean opposite things:
 *
 *   Failed    the employee sat the exam and did not pass
 *   Rejected  an approver decided not to reimburse
 *
 * An employee can fail and still be reimbursed; an employee can pass and be declined. So the
 * result lives here as CertificationResult, and the approval status stays exactly where it
 * already was, in ExpenseStatus. Nothing in this file restates an approval state.
 *
 * ── Why the record is separate from the Expense ───────────────────────────────────────────
 *
 * A reimbursement is an Expense — same table, same Manager → Finance flow, same approval rows.
 * The certification is not: it outlives the application, it exists without one (a certification
 * somebody already holds, added straight to their history), and a failed attempt keeps its own
 * dates and document after a later attempt passes. Those are facts about a qualification, not
 * about a payment, so they are stored as their own record and the Expense points at it.
 *
 * That is also what stops the Expense model growing eight columns only one category ever uses.
 */

/**
 * Whether the employee passed the exam.
 *
 * Two values only. There is no PENDING: a certification record is created from a result that has
 * already happened, and "waiting to hear" is the absence of a record rather than a state of one.
 */
export enum CertificationResult {
  PASSED = 'Passed',
  FAILED = 'Failed',
}

/**
 * The Certification Admin's decision, which is a stage of its own.
 *
 * ── Why this is not an ExpenseStatus ──────────────────────────────────────────────────────
 *
 * The flow for a certification claim has FOUR gates, not two:
 *
 *   Employee submits → Manager → Certification Admin → Finance Admin → Reimbursed
 *
 * Only certifications have the third one. ExpenseStatus is shared by every category and is read
 * by ten dashboards, the workflow state machine and several property tests that enumerate it —
 * adding a value there would put a state on travel claims and meal claims that can never occur,
 * and would change existing reimbursement behaviour, which this module is required not to do.
 *
 * So the extra gate lives HERE, on the certification, and the expense stays at MANAGER_APPROVED
 * while it is decided. The expense's own status is untouched until Finance acts on it, which is
 * exactly what "the shared workflow is unchanged" means in practice.
 */
export enum CertAdminDecision {
  /** Manager approved it; the Certification Admin has not looked yet. */
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
}

/** Where a certification record came from, which decides what it is allowed to do. */
export enum CertificationSource {
  /** Added by the employee straight to their history. No approval, no reimbursement. */
  DIRECT = 'Direct',
  /** Raised through Apply for Reimbursement, so an Expense references it. */
  REIMBURSEMENT = 'Reimbursement',
}

/**
 * One sitting of the exam.
 *
 * The history the requirement insists on keeping: when an employee fails and later passes, the
 * failed attempt is NOT overwritten. It stays as its own entry with its own dates and its own
 * document, and the passing attempt is appended. Anything that needs "what happened" reads the
 * attempts; anything that needs "where do they stand now" reads the record's own result.
 */
export interface CertificationAttempt {
  id: string;
  result: CertificationResult;
  /** The day the exam was sat. */
  examDate?: string;
  /** The day the result was published. Often the same day; frequently not. */
  resultDate: string;
  /** S3 key of the pass certificate or the failed result document. Required either way. */
  documentKey: string;
  documentName?: string;
  /** For a failed attempt, the day by which the retake must happen. See retakeDeadline. */
  retakeDeadline?: string;
  recordedAt: string;
  recordedBy: string;
}

export interface Certification {
  id: string;
  /** Canonical email of the employee the certification belongs to. The ownership boundary. */
  employeeEmail: string;
  employeeName?: string;
  employeeId?: string;
  name: string;
  provider: string;
  /** The CURRENT standing — the latest attempt's result. Attempts hold the full history. */
  result: CertificationResult;
  examDate?: string;
  resultDate: string;
  /** Set only while the latest attempt is a failure. Cleared when a retake passes. */
  retakeDeadline?: string;
  /** The day the employee actually passed. Absent until they do. */
  completionDate?: string;
  /**
   * When the certification stops being valid.
   *
   * Most vendor certifications lapse — AWS and Azure run three-year cycles, others one or two —
   * and an expired certification is not the same as one somebody holds. Optional because plenty
   * never expire at all: a degree, an internal qualification. Absent means "does not expire",
   * which is why it is not defaulted to anything.
   *
   * Only meaningful on a PASSED certification. A failed attempt has nothing to expire, and the
   * form hides the field accordingly.
   */
  expiryDate?: string;
  /** The document backing the CURRENT result. Every attempt keeps its own in `attempts`. */
  documentKey?: string;
  documentName?: string;
  attempts: CertificationAttempt[];
  source: CertificationSource;
  /** The reimbursement application this came from, when it came from one. */
  expenseId?: string;
  /**
   * The Certification Admin gate. Present only on a record raised through reimbursement —
   * a certification somebody simply recorded has nothing to approve.
   */
  certAdminDecision?: CertAdminDecision;
  certAdminBy?: string;
  certAdminAt?: string;
  certAdminNote?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * The retake deadline: three CALENDAR months after the result.
 *
 * Calendar months, not 90 days — the requirement is explicit, and the two differ by up to two
 * days depending on which months are involved. 10 September → 10 December, not 9 December.
 *
 * ── End-of-month clamping ─────────────────────────────────────────────────────────────────
 *
 * 30 November + 3 months has no 30 February to land on. JavaScript's Date would roll that
 * forward into March, quietly granting two extra days; this clamps to the last day of the target
 * month instead, which is the reading every calendar application uses and the one an employee
 * would expect. 30 November 2026 → 28 February 2027.
 *
 * Returns undefined for an unparseable date rather than inventing a deadline — a wrong deadline
 * is worse than a missing one, because somebody would plan around it.
 */
export const RETAKE_WINDOW_MONTHS = 3;

export function calculateRetakeDeadline(
  resultDate: string,
  months: number = RETAKE_WINDOW_MONTHS
): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(resultDate || '').trim());
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]); // 1-12
  const day = Number(match[3]);

  const targetMonthIndex = month - 1 + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = (targetMonthIndex % 12) + 1;

  // Day 0 of the following month is the last day of the target month — the clamp described above.
  const lastDayOfTarget = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  const clampedDay = Math.min(day, lastDayOfTarget);

  return `${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`;
}

/**
 * How long is left to retake, counted to the END of the deadline day.
 *
 * ── Why end-of-day, and why local ─────────────────────────────────────────────────────────
 *
 * The deadline is a plain date, and an employee whose deadline is 10 December has the whole of
 * the 10th to sit the exam. Counting to midnight at the START of that day would tell them they
 * were out of time for the entire last day they actually have — and would disagree with
 * isRetakeOverdue, which does not call a certification overdue until the day after.
 *
 * Local rather than UTC because the person reading it is doing so on their own clock. Built from
 * the date parts through the Date constructor, never by parsing "2026-12-10" as an instant: that
 * form is treated as UTC midnight, which lands on the 9th for anyone west of Greenwich and would
 * silently remove a day from the window.
 *
 * ── Minutes, not seconds ──────────────────────────────────────────────────────────────────
 *
 * The display stops at minutes, so a ticking clock does not turn a three-month deadline into a
 * stopwatch. `totalMs` is returned for callers that want to decide urgency on the real figure
 * rather than on the rounded parts.
 */
export interface RetakeTimeRemaining {
  expired: boolean;
  days: number;
  hours: number;
  minutes: number;
  totalMs: number;
}

export function retakeTimeRemaining(
  deadline: string | undefined,
  now: Date = new Date()
): RetakeTimeRemaining | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(deadline || '').trim());
  if (!match) return undefined;

  const endOfDeadlineDay = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    23,
    59,
    59,
    999
  );

  const totalMs = endOfDeadlineDay.getTime() - now.getTime();
  if (totalMs <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, totalMs: 0 };

  const totalMinutes = Math.floor(totalMs / 60000);
  return {
    expired: false,
    days: Math.floor(totalMinutes / (60 * 24)),
    hours: Math.floor((totalMinutes % (60 * 24)) / 60),
    minutes: totalMinutes % 60,
    totalMs,
  };
}

/**
 * How loudly to show the remaining time.
 *
 * A three-month deadline is information; a three-day one is something to act on today. The
 * thresholds are days of REAL time left, not calendar arithmetic, so a deadline 24 hours away
 * reads as urgent whichever side of midnight it falls on.
 */
export type RetakeUrgency = 'expired' | 'critical' | 'warning' | 'normal';

export function retakeUrgency(remaining?: RetakeTimeRemaining): RetakeUrgency {
  if (!remaining) return 'normal';
  if (remaining.expired) return 'expired';
  if (remaining.days < 7) return 'critical';
  if (remaining.days < 30) return 'warning';
  return 'normal';
}

/** "89 days 7 hours 32 minutes", or the plain statement that it is over. */
export function formatRetakeRemaining(remaining?: RetakeTimeRemaining): string {
  if (!remaining) return '—';
  if (remaining.expired) return 'Retake Deadline Passed';

  const plural = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`;
  /*
   * Days are dropped once there are none, rather than printed as "0 days".
   *
   * On the last day the useful reading is "7 hours 32 minutes"; a leading zero pushes the part
   * that matters to the right and makes the line scan as though it were still measured in days.
   */
  const parts = remaining.days > 0 ? [plural(remaining.days, 'day')] : [];
  parts.push(plural(remaining.hours, 'hour'), plural(remaining.minutes, 'minute'));
  return parts.join(' ');
}

/**
 * "89d 7h" — the same figure, for a table cell.
 *
 * Compact rather than a second calculation: it reads the SAME RetakeTimeRemaining the block
 * display uses, so the row and the panel below it can never disagree about how long is left.
 *
 * Minutes are dropped here on purpose. A table is scanned rather than read, days and hours are
 * what a reader compares between rows, and a value that changed every minute in a list of ten
 * would draw the eye to the wrong thing.
 */
export function formatRetakeRemainingCompact(remaining?: RetakeTimeRemaining): string {
  if (!remaining) return '—';
  if (remaining.expired) return 'Passed';
  if (remaining.days > 0) return `${remaining.days}d ${remaining.hours}h`;
  return `${remaining.hours}h ${remaining.minutes}m`;
}

/** Whether a failed certification's retake window has closed, as of `today`. */
export function isRetakeOverdue(certification: Certification, today: string): boolean {
  if (certification.result !== CertificationResult.FAILED) return false;
  if (!certification.retakeDeadline) return false;
  return today > certification.retakeDeadline;
}

/**
 * What a certification form was filled in with, before it becomes a record.
 *
 * Separate from Certification because a draft has no id, no attempts and no timestamps, and a
 * type that made those optional would make every reader check fields the stored record always
 * has.
 */
export interface CertificationDraft {
  name: string;
  provider: string;
  result: CertificationResult;
  examDate?: string;
  resultDate: string;
  /** Only collected for a passed certification — see Certification.expiryDate. */
  expiryDate?: string;
  documentKey?: string;
  documentName?: string;
  /** Admins only. Absent means "the signed-in employee", which is the rule for everyone else. */
  employeeEmail?: string;
}

/**
 * How a certification's validity stands, as of `today`.
 *
 * Three states rather than a boolean, because "expires in a fortnight" and "expired last year"
 * need different treatment and lumping them together loses the one that is actionable. A
 * certification with no expiry date is PERMANENT — not "valid", because the two answer different
 * questions and only one of them can ever change.
 */
export type CertificationValidity = 'permanent' | 'valid' | 'expiring' | 'expired';

/** Inside this many days of the expiry date, a certification is "expiring" rather than valid. */
export const EXPIRY_WARNING_DAYS = 60;

export function certificationValidity(
  certification: Pick<Certification, 'result' | 'expiryDate'>,
  today: string
): CertificationValidity {
  /*
   * A failed certification is never "expired".
   *
   * It was never valid to begin with, and reporting it as expired would put it in a list of
   * things to renew — when what it actually needs is a retake.
   */
  if (certification.result !== CertificationResult.PASSED) return 'permanent';
  const expiry = String(certification.expiryDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(expiry)) return 'permanent';

  if (expiry < today) return 'expired';

  /*
   * Warning window measured in real days, not by comparing month numbers.
   *
   * A string compare would make "expires next month" mean something different on the 1st than
   * on the 28th. Built from UTC parts so a timezone cannot shift the boundary by a day.
   */
  const [ey, em, ed] = expiry.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  const daysLeft = Math.round(
    (Date.UTC(ey, em - 1, ed) - Date.UTC(ty, tm - 1, td)) / 86400000
  );
  return daysLeft <= EXPIRY_WARNING_DAYS ? 'expiring' : 'valid';
}

/** Whole days until a certification expires. Negative once it has. Undefined when permanent. */
export function daysUntilExpiry(expiryDate: string | undefined, today: string): number | undefined {
  const expiry = String(expiryDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(expiry) || !/^\d{4}-\d{2}-\d{2}$/.test(today)) return undefined;
  const [ey, em, ed] = expiry.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  return Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(ty, tm - 1, td)) / 86400000);
}

/**
 * What is wrong with a draft, as a list of sentences.
 *
 * A list rather than the first failure, so a form shows every problem at once instead of making
 * somebody rediscover them one save at a time — the same shape validatePolicyInput uses.
 *
 * The rule the requirement is most emphatic about: a document is mandatory for BOTH results. A
 * failed attempt without its result document is not a lighter record, it is an unevidenced claim
 * that an exam was sat at all, and the retake deadline is calculated from a date inside it.
 */
export function validateCertificationDraft(draft: Partial<CertificationDraft>): string[] {
  const errors: string[] = [];

  if (!String(draft.name || '').trim()) errors.push('Certification name is required.');
  if (!String(draft.provider || '').trim()) errors.push('Certification provider is required.');

  if (draft.result !== CertificationResult.PASSED && draft.result !== CertificationResult.FAILED) {
    errors.push('Select whether the certification was passed or failed.');
  }

  const isoDate = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDate.test(String(draft.resultDate || ''))) {
    errors.push('Result date is required.');
  }
  if (draft.examDate && !isoDate.test(draft.examDate)) {
    errors.push('Exam date must be a valid date.');
  }
  if (draft.examDate && draft.resultDate && draft.examDate > draft.resultDate) {
    // A result cannot predate the exam that produced it; usually a mistyped year.
    errors.push('The result date cannot be before the exam date.');
  }

  if (draft.expiryDate) {
    if (!isoDate.test(draft.expiryDate)) {
      errors.push('Expiry date must be a valid date.');
    } else if (draft.resultDate && draft.expiryDate <= draft.resultDate) {
      // A certification cannot lapse before it was earned; almost always a mistyped year.
      errors.push('The expiry date must be after the result date.');
    }
  }

  if (!String(draft.documentKey || '').trim()) {
    errors.push(
      draft.result === CertificationResult.FAILED
        ? 'Upload the result document. It is required for a failed attempt as well as a pass.'
        : 'Upload the certificate.'
    );
  }

  return errors;
}
